const ALLOWED_HTML_TAGS = new Set(['a', 'b', 'br', 'em', 'i', 'li', 'ol', 'p', 'span', 'strong', 'u', 'ul']);
const URL_SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

export const sanitizeTrustedHtml = (rawHtml?: string): string => {
    if (!rawHtml) {
        return '';
    }

    const parser = new DOMParser();
    const parsed = parser.parseFromString(rawHtml, 'text/html');

    const sanitizeNode = (node: ChildNode): void => {
        const childNodes = Array.from(node.childNodes);

        childNodes.forEach(childNode => {
            if (childNode.nodeType === globalThis.Node.COMMENT_NODE) {
                childNode.parentNode?.removeChild(childNode);
                return;
            }

            if (childNode.nodeType !== globalThis.Node.ELEMENT_NODE) {
                return;
            }

            const element = childNode as HTMLElement;
            const tagName = element.tagName.toLowerCase();

            if (!ALLOWED_HTML_TAGS.has(tagName)) {
                const parent = element.parentNode;
                if (parent) {
                    while (element.firstChild) {
                        parent.insertBefore(element.firstChild, element);
                    }
                    parent.removeChild(element);
                }
                return;
            }

            Array.from(element.attributes).forEach(attribute => {
                const attributeName = attribute.name.toLowerCase();

                if (attributeName.startsWith('on') || attributeName === 'style' || attributeName === 'srcdoc') {
                    element.removeAttribute(attribute.name);
                    return;
                }

                const isAllowedAnchorAttribute = tagName === 'a' && ['href', 'target', 'rel', 'title'].includes(attributeName);
                const isAllowedSpanClassAttribute = tagName === 'span' && attributeName === 'class';

                if (!isAllowedAnchorAttribute && !isAllowedSpanClassAttribute) {
                    element.removeAttribute(attribute.name);
                }
            });

            if (tagName === 'a') {
                const href = element.getAttribute('href');
                if (href) {
                    const trimmedHref = href.trim();
                    const isHashLink = trimmedHref.startsWith('#');
                    const isRelativeLink = trimmedHref.startsWith('/');

                    let isSafeHref = isHashLink || isRelativeLink;
                    if (!isSafeHref) {
                        try {
                            const parsedUrl = new URL(trimmedHref, window.location.origin);
                            isSafeHref = URL_SAFE_PROTOCOLS.has(parsedUrl.protocol);
                        } catch {
                            isSafeHref = false;
                        }
                    }

                    if (!isSafeHref) {
                        element.removeAttribute('href');
                    }
                }

                const target = element.getAttribute('target');
                if (target !== '_blank' && target !== '_self') {
                    element.removeAttribute('target');
                }

                if (element.getAttribute('target') === '_blank') {
                    element.setAttribute('rel', 'noopener noreferrer');
                } else {
                    element.removeAttribute('rel');
                }
            }

            sanitizeNode(element);
        });
    };

    sanitizeNode(parsed.body);
    return parsed.body.innerHTML;
};

export const normalizeRichTextToHtml = (richTextContent?: unknown): string => {
    if (typeof richTextContent === 'string') {
        return richTextContent;
    }

    if (richTextContent && typeof richTextContent === 'object') {
        const richTextObject = richTextContent as {
            html?: unknown;
            text?: unknown;
            value?: unknown;
        };

        if (typeof richTextObject.html === 'string') {
            return richTextObject.html;
        }

        if (typeof richTextObject.text === 'string') {
            return richTextObject.text;
        }

        if (typeof richTextObject.value === 'string') {
            return richTextObject.value;
        }
    }

    return '';
};

export const sanitizeRichTextHtml = (richTextContent?: unknown): string => {
    return sanitizeTrustedHtml(normalizeRichTextToHtml(richTextContent));
};